import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { NotificationService } from '@services';
import {
  PlanktonEvent,
  PlanktonOccurrence,
  PlanktonOrigin,
  PlanktonTaxon,
} from '../models/plankton.model';
import { catchError, of } from 'rxjs';
import { environment } from '@environment';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

export const planktonEventResolver: ResolveFn<PlanktonEvent | null> = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const notificationService = inject(NotificationService);

  const id = route.paramMap.get('id');
  if (!id) {
    return of(null);
  }

  const endpoint = `${environment.kinkoHost}/plankton/event/${id}?occurrenceCount=true`;

  return http.get<PlanktonEvent>(endpoint, httpOptions).pipe(
    catchError((err) => {
      if (err?.status == 404) {
        router
          .navigate(['/404'], {
            skipLocationChange: true,
          })
          .finally(() => {
            notificationService.error('Failed to load the plankton event.');
          });
      }

      return of(null);
    }),
  );
};

export const planktonOccurrenceResolver: ResolveFn<
  PlanktonOccurrence | null
> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const notificationService = inject(NotificationService);

  const id = route.paramMap.get('id');
  if (!id) {
    return of(null);
  }

  const endpoint = `${environment.kinkoHost}/plankton/occurrence/${id}?event=true&taxon=true`;

  return http.get<PlanktonOccurrence>(endpoint, httpOptions).pipe(
    catchError((err) => {
      console.error(err);

      if (err?.status == 404) {
        router
          .navigate(['/404'], {
            skipLocationChange: true,
          })
          .finally(() => {
            notificationService.error(
              'Failed to load the plankton occurrence.',
            );
          });
      }

      return of(null);
    }),
  );
};

export const planktonOriginResolver: ResolveFn<PlanktonOrigin | null> = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const notificationService = inject(NotificationService);

  const id = route.paramMap.get('id');
  if (!id) {
    return of(null);
  }

  const endpoint = `${environment.kinkoHost}/plankton/origin/${id}`;

  return http.get<PlanktonOrigin>(endpoint, httpOptions).pipe(
    catchError((err) => {
      console.error(err);

      if (err?.status == 404) {
        router
          .navigate(['/404'], {
            skipLocationChange: true,
          })
          .finally(() => {
            notificationService.error('Failed to load the plankton origin.');
          });
      }

      return of(null);
    }),
  );
};

export const planktonTaxonResolver: ResolveFn<PlanktonTaxon | null> = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const http = inject(HttpClient);
  const notificationService = inject(NotificationService);

  const id = route.paramMap.get('id');
  if (!id) {
    return of(null);
  }

  const endpoint = `${environment.kinkoHost}/plankton/taxon/${id}?occurrenceCount=true`;

  return http.get<PlanktonTaxon>(endpoint, httpOptions).pipe(
    catchError((err) => {
      console.error(err);

      if (err?.status == 404) {
        router
          .navigate(['/404'], {
            skipLocationChange: true,
          })
          .finally(() => {
            notificationService.error('Failed to load the plankton taxon.');
          });
      }

      return of(null);
    }),
  );
};
